import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { useNavItems } from '../../../hooks';
import { useModuleOperators } from '../../../hooks/useModuleOperators';
import {
  useRemunerationGroups,
  useAddComment,
  type RemunerationGroupsResponse,
  type RemunerationGroupRow,
} from './hooks';
import { RemunerationTable } from './components/RemunerationTable';
import { CommentModal } from './components/CommentModal';
import NotificationSnackbar from '../../../common/components/Snackbar/Snackbar';
import { NoDataFound } from '../../../common/components/NoDataFound';
import { ContextMenuAction } from '../../../common/components/Table/types';
import {
  MainContent,
  ReportContainer,
  ButtonContainer,
  FilterSection,
  SelectContainer,
  StyledSegmentedControl,
  StyledSegmentButton,
  ClearResultsButton,
  SearchButton,
} from './RemunerationGroups.styled';

interface DirectionOption {
  id: string;
  label: string;
}

function RemunerationGroups() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const navItems = useNavItems();
  const [searchParams, setSearchParams] = useSearchParams();

  const [ppoValue, setPpoValue] = useState('');
  const [ppoFilter, setPpoFilter] = useState('All');
  const [selectedYear, setSelectedYear] = useState('');

  // Add state for results - can be either combined array or separate INBOUND/OUTBOUND
  const [results, setResults] = useState<RemunerationGroupRow[] | null>(null);
  const [inboundResults, setInboundResults] = useState<RemunerationGroupRow[] | null>(null);
  const [outboundResults, setOutboundResults] = useState<RemunerationGroupRow[] | null>(null);
  console.log('results', results);
  // Add state to track when no data is found
  const [noDataFound, setNoDataFound] = useState<boolean>(false);

  // Comment modal state
  const [commentModal, setCommentModal] = useState<{
    open: boolean;
    row: RemunerationGroupRow | null;
  }>({
    open: false,
    row: null,
  });

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'warning' | 'info' | 'success',
  });

  // Fetch eno1 module effective operators for PPO dropdown
  const {
    operators,
    availableYears,
    isPpoLoading,
    ppoError,
  } = useModuleOperators('eno1', { selectedYear, selectedPpo: ppoValue || undefined });

  // Remuneration Groups hook
  const {
    mutate: searchRemunerationGroups,
    isPending: isSearching,
    error: searchError,
    reset: resetSearchError,
  } = useRemunerationGroups();

  // Add Comment hook
  const {
    mutate: addComment,
    isPending: isAddingComment,
    error: commentError,
    reset: resetCommentError,
  } = useAddComment();

  // Extract enabled target DOs from effective operators
  const ppoOptions = React.useMemo(() => {
    return operators.map(op => ({
      id: op,
      label: op,
      hasLicense: false,
    }));
  }, [operators]);

  // Generate year options from available years
  const yearOptions = React.useMemo(() => {
    return availableYears.map(year => ({
      value: year.toString(),
      label: year.toString(),
    }));
  }, [availableYears]);

  // Direction filter options
  const directionOptions: DirectionOption[] = [
    { id: 'All', label: 'All' },
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/ipc-td-accounting');
  };

  const handleDirectionChange = (id: string) => {
    setPpoFilter(id);
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const showSnackbar = (
    message: string,
    severity: 'error' | 'warning' | 'info' | 'success' = 'error'
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleReset = () => {
    setPpoValue('');
    setPpoFilter('Inbound');
    setSelectedYear('');
    // Clear results when resetting
    setResults(null);
    setInboundResults(null);
    setOutboundResults(null);
    setNoDataFound(false);
    // Clear URL parameters when resetting
    setSearchParams(new URLSearchParams());
  };

  const handleSearch = () => {
    // Reset any previous errors
    resetSearchError();

    // Validate required fields
    if (!selectedYear) {
      showSnackbar('Please select a Year');
      return;
    }

    // Map direction filter to API format
    const directionMap: { [key: string]: 'INBOUND' | 'OUTBOUND' | 'ALL' } = {
      Inbound: 'INBOUND',
      Outbound: 'OUTBOUND',
      All: 'ALL',
    };

    // Prepare API parameters
    const params = {
      direction: directionMap[ppoFilter],
      year: parseInt(selectedYear),
      receivingPpo: ppoValue || 'ALL', // Use 'ALL' if no PPO is selected
    };

    searchRemunerationGroups(params, {
      onSuccess: (response) => {
        console.log('Remuneration Groups search results:', response.data);
        console.log('Current filter params used:', params);
        console.log('Response structure:', response.data);

        // The API now returns an object with INBOUND/OUTBOUND keys
        // Extract the array based on the selected direction
        let dataArray: any[] = [];

        if (response.data) {
          // Get the direction key (INBOUND, OUTBOUND, or ALL)
          const directionKey = params.direction;

          if (directionKey === 'ALL') {
            // If ALL is selected, keep INBOUND and OUTBOUND arrays separate
            const inboundData = (response.data as any).INBOUND || [];
            const outboundData = (response.data as any).OUTBOUND || [];
            
            console.log('Inbound data array:', inboundData);
            console.log('Outbound data array:', outboundData);
            console.log('Inbound length:', inboundData.length);
            console.log('Outbound length:', outboundData.length);
            
            // Check if both arrays are empty
            if (inboundData.length === 0 && outboundData.length === 0) {
              setNoDataFound(true);
              setResults(null);
              setInboundResults(null);
              setOutboundResults(null);
              return;
            }

            // Process both arrays separately
            const processedInbound = inboundData.map((item: any) => ({
              ...item,
              sendingPpoCode: item.sendingPpo?.name || '-',
              direction: 'INBOUND',
            }));

            const processedOutbound = outboundData.map((item: any) => ({
              ...item,
              sendingPpoCode: item.sendingPpo?.name || '-',
              direction: 'OUTBOUND',
            }));

            setNoDataFound(false);
            setResults(null); // Clear combined results when showing separate tables
            setInboundResults(processedInbound);
            setOutboundResults(processedOutbound);
          } else {
            // Get data for the specific direction
            dataArray = (response.data as any)[directionKey] || [];
            
            console.log('Extracted data array:', dataArray);
            console.log('Array length:', dataArray.length);
            console.log('First item structure:', dataArray[0]);
            console.log('Direction key used:', params.direction);
            
            // Check for empty data
            if (!dataArray || dataArray.length === 0) {
              setNoDataFound(true);
              setResults(null);
              setInboundResults(null);
              setOutboundResults(null);
              return;
            }

            // Add computed fields for the table
            const processedData = dataArray.map((item: any) => ({
              ...item,
              sendingPpoCode: item.sendingPpo?.name || '-',
              direction: directionKey,
            }));

            setNoDataFound(false);
            setResults(processedData);
            setInboundResults(null);
            setOutboundResults(null);
          }
        }
      },
      onError: (error) => {
        console.error('Error searching remuneration groups:', error);
      },
    });
  };

  // Memoize clearResults callback
  const clearResults = useCallback(() => {
    setResults(null);
    setInboundResults(null);
    setOutboundResults(null);
    setNoDataFound(false);
    // Clear URL parameters when clearing results
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Use ref to track if we've already synced URL parameters
  const hasSyncedUrlParams = useRef(false);

  // Initial URL parameter sync when page loads
  useEffect(() => {
    // Only sync once and when yearOptions are available
    if (hasSyncedUrlParams.current || yearOptions.length === 0) {
      return;
    }

    const ppoParam = searchParams.get('ppo');
    const yearParam = searchParams.get('year');
    const directionParam = searchParams.get('direction');

    // Sync PPO parameter
    if (ppoParam) {
      setPpoValue(ppoParam === 'All' ? '' : ppoParam);
    }

    // Sync direction filter
    if (directionParam) {
      setPpoFilter(directionParam);
    }

    // Sync year parameter
    if (yearParam && yearParam !== 'all') {
      setSelectedYear(yearParam);
    }

    // Mark as synced
    hasSyncedUrlParams.current = true;
  }, [searchParams, yearOptions]); // Depend on yearOptions to ensure they're available

  // Handler functions for SectionBar custom filter changes
  const handleSectionBarPpoChange = useCallback((ppo: string) => {
    setPpoValue(ppo === 'All' ? '' : ppo);
  }, []);

  const handleSectionBarYearChange = useCallback((year: string) => {
    setSelectedYear(year === 'all' ? '' : year);
  }, []);

  const handleSectionBarDirectionChange = useCallback((direction: string) => {
    setPpoFilter(direction);
  }, []);

  // Comment modal handlers
  const handleCloseCommentModal = useCallback(() => {
    setCommentModal({
      open: false,
      row: null,
    });
    resetCommentError();
  }, [resetCommentError]);

  const handleSaveComment = useCallback((comment: string, onSuccess?: () => void) => {
    if (!commentModal.row?.id) {
      showSnackbar('Unable to save comment: Row ID not found', 'error');
      return;
    }

    console.log('Sending comment for row ID:', commentModal.row.id);
    console.log('Comment text:', comment);

    addComment(
      {
        rgSenderId: commentModal.row.id,
        rgComment: comment,
      },
      {
        onSuccess: (response) => {
          console.log('Comment added successfully:', response);
          showSnackbar('Comment added successfully', 'success');
          // Call the onSuccess callback to refresh comments in modal
          if (onSuccess) {
            onSuccess();
          }
          // Don't close modal immediately, let user see the new comment
        },
        onError: (error) => {
          console.error('Error adding comment:', error);
          showSnackbar('Failed to add comment. Please try again.', 'error');
        },
      }
    );
  }, [commentModal.row, addComment, showSnackbar]);

  // Context menu handlers
  const handleContextMenuAction = useCallback(
    (action: ContextMenuAction, row: RemunerationGroupRow) => {
      console.log('Context menu action:', action, 'for row:', row);

      switch (action) {
        case 'editSamples':
          // Navigate to edit page with the selected row data
          showSnackbar('Redirecting to edit page with your selection...', 'info');
          navigate('/remuneration-groups/edit', {
            state: {
              // Pass the complete remuneration group object for better data handling
              remunerationGroup: {
                id: row.remunerationGroup?.id,
                name: row.remunerationGroup?.name,
                displayName: row.remunerationGroup?.displayName,
                operator: row.remunerationGroup?.operator,
                predefined: row.remunerationGroup?.predefined,
                homeCountry: row.remunerationGroup?.homeCountry,
              },
              year: row.year,
              receivingPpo: row.receivingPpo?.name || row.receivingPpo,
              // Pass additional data for better context
              direction: ppoFilter,
              ppoValue: ppoValue || 'ALL',
            },
          });
          break;
        case 'comment':
          // Open comment modal
          console.log('Opening comment modal for row:', row);
          console.log('Row ID:', row.id);
          setCommentModal({
            open: true,
            row: row,
          });
          break;
        default:
          console.log('Action not implemented:', action);
          break;
      }
    },
    [navigate, showSnackbar, ppoFilter, ppoValue]
  );

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column' }}
      data-testid='remuneration-groups-container'
    >
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='Remuneration Groups'
            subtitle='IPC TD Accounting'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            data-testid='section-bar'
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar
                isOpen={sidebarOpen}
                onToggle={handleSidebarToggle}
                navItems={navItems}
                data-testid='remuneration-groups-sidebar'
              />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='remuneration-groups-main-content'>
            <ReportContainer data-testid='remuneration-groups-content'>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
                data-testid='remuneration-groups-header'
              >
                <Typography
                  variant='h4'
                  sx={{ fontWeight: 600, color: '#063142' }}
                  data-testid='remuneration-groups-title'
                >
                  Remuneration Groups
                </Typography>
                <Box
                  sx={{ display: 'flex', gap: 1 }}
                  data-testid='remuneration-groups-header-actions'
                >
                  {(results || inboundResults || outboundResults || noDataFound) && (
                    <ClearResultsButton
                      variant='outlined'
                      onClick={clearResults}
                      data-testid='remuneration-groups-clear-results-btn'
                    >
                      Clear Results
                    </ClearResultsButton>
                  )}
                </Box>
              </Box>

              {/* Error Alert */}
              {searchError && (
                <Alert severity='error' sx={{ mb: 2 }} onClose={() => resetSearchError()}>
                  {searchError.message || 'An error occurred while searching remuneration groups'}
                </Alert>
              )}

              {/* Edit/Manage RGs Button */}
              <Box sx={{ mb: 3 }}>
                <Button
                  variant='contained'
                  sx={{
                    backgroundColor: '#00ACE7',
                    color: '#FFFFFF',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#0095C7',
                    },
                  }}
                  onClick={() => {
                    showSnackbar('Navigating to edit page. Please select your criteria to search.', 'info');
                    navigate('/remuneration-groups/edit', {
                      state: {
                        // Pass current filter values for better context
                        year: selectedYear,
                        direction: ppoFilter,
                        ppoValue: ppoValue || 'ALL',
                        // Note: No remuneration group selected from main page, user will need to select one
                      },
                    });
                  }}
                >
                  Edit/Manage RGs
                </Button>
              </Box>

              {/* PPO Section */}
              <FilterSection>
                {/* PPO Dropdown and Direction Filter in one row */}
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                      DO
                    </Typography>
                    <SelectContainer>
                      <FormControl size='small' fullWidth>
                        <Select
                          value={ppoValue}
                          onChange={(e) => setPpoValue(e.target.value)}
                          displayEmpty
                          sx={{ minWidth: 160 }}
                          disabled={isPpoLoading}
                        >
                          <MenuItem value=''>
                            {isPpoLoading ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} />
                                <span>Loading...</span>
                              </Box>
                            ) : (
                              'All'
                            )}
                          </MenuItem>
                          {!isPpoLoading &&
                            !ppoError &&
                            ppoOptions.map((option) => (
                              <MenuItem key={option.id} value={option.id}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                  }}
                                >
                                  <span>{option.label}</span>
                                 
                                </Box>
                              </MenuItem>
                            ))}
                          {ppoError && <MenuItem disabled>Error loading PPOs</MenuItem>}
                        </Select>
                      </FormControl>
                    </SelectContainer>
                  </Box>

                  {/* Direction Filter using Segmented Control */}
                  <StyledSegmentedControl>
                    {directionOptions.map((option) => (
                      <StyledSegmentButton
                        key={option.id}
                        active={ppoFilter === option.id}
                        onClick={() => handleDirectionChange(option.id)}
                        sx={{
                          color: ppoFilter === option.id ? '#ffffff' : '#00B6BA',
                          minWidth: '70px',
                        }}
                      >
                        {option.label}
                      </StyledSegmentButton>
                    ))}
                  </StyledSegmentedControl>
                </Box>

                {/* Divider */}
                <Box
                  sx={{
                    height: '1px',
                    backgroundColor: '#E1E8ED',
                    mb: 2,
                  }}
                />

                {/* Year Selector */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                      Year: *
                    </Typography>
                    <SelectContainer>
                      <FormControl size='small' fullWidth>
                        <Select
                          value={selectedYear}
                          onChange={(e) => setSelectedYear(e.target.value)}
                          displayEmpty
                          sx={{ minWidth: 184 }}
                        >
                          <MenuItem value=''>Select</MenuItem>
                          {yearOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </SelectContainer>
                  </Box>
                </Box>
              </FilterSection>

              {/* Action Buttons */}
              <ButtonContainer sx={{ justifyContent: 'flex-end' }}>
                <SearchButton variant='contained' onClick={handleSearch} disabled={isSearching}>
                  {isSearching ? <CircularProgress size={16} /> : 'SEARCH'}
                </SearchButton>
              </ButtonContainer>

              {/* Results Section */}
              {results && !noDataFound && (
                <Box sx={{ mt: 6 }}>
                  {/* Results Table */}
                  <Box
                    sx={{
                      '& .MuiPaper-root': {
                        boxShadow: 'none',
                        border: '1px solid #E1E8ED',
                      },
                    }}
                  >
                    <RemunerationTable
                      data={results}
                      onContextMenuAction={handleContextMenuAction}
                    />
                  </Box>
                </Box>
              )}

              {/* Separate Tables for INBOUND and OUTBOUND when direction is 'All' */}
              {(inboundResults || outboundResults) && !noDataFound && (
                <Box sx={{ mt: 6 }}>
                  {/* INBOUND Table */}
                  {inboundResults && inboundResults.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#2e7d32',
                          mb: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        ↓ INBOUND ({inboundResults.length} records)
                      </Typography>
                      <Box
                        sx={{
                          '& .MuiPaper-root': {
                            boxShadow: 'none',
                            border: '1px solid #E1E8ED',
                          },
                        }}
                      >
                        <RemunerationTable
                          data={inboundResults}
                          onContextMenuAction={handleContextMenuAction}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* OUTBOUND Table */}
                  {outboundResults && outboundResults.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#c62828',
                          mb: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        ↑ OUTBOUND ({outboundResults.length} records)
                      </Typography>
                      <Box
                        sx={{
                          '& .MuiPaper-root': {
                            boxShadow: 'none',
                            border: '1px solid #E1E8ED',
                          },
                        }}
                      >
                        <RemunerationTable
                          data={outboundResults}
                          onContextMenuAction={handleContextMenuAction}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Comment Modal */}
      <CommentModal
        open={commentModal.open}
        onClose={handleCloseCommentModal}
        onSave={handleSaveComment}
        row={commentModal.row}
        isLoading={isAddingComment}
      />

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid='remuneration-groups-snackbar'
      />
    </Box>
  );
}

export default RemunerationGroups;
